import type { OriginUpdateType } from '@/types/origin'

const SPEC_STEPS = {
  always: ['design', 'estimation', 'production_file', 'outsource_file', 'web_image', 'product_review', 'pim_publish', 'piq_quickpart', 'web_publish'],
  conditional: ['signagent_svg', 'instructions', 'assembled_view', 'install_notes', 'hardware'],
}
const DESIGN_STEPS = {
  always: ['design', 'sales_approval', 'production_file', 'outsource_file', 'product_review', 'pim_publish', 'piq_quickpart', 'web_publish'],
  conditional: ['signagent_svg', 'web_image', 'instructions', 'assembled_view'],
}

export const MODIFY_DELIVERABLES: Record<
  OriginUpdateType,
  { always: string[]; conditional: string[] }
> = {
  price: { always: ['pim_publish', 'piq_quickpart', 'web_publish'], conditional: [] },
  uin: {
    always: ['uin', 'production_file', 'outsource_file', 'pim_publish', 'piq_quickpart', 'web_publish'],
    conditional: [],
  },
  web: { always: ['web_publish'], conditional: [] },
  spec: SPEC_STEPS,
  design: DESIGN_STEPS,
  sb2: SPEC_STEPS,
  sb3: DESIGN_STEPS,
  sb4: SPEC_STEPS,
  sb5: SPEC_STEPS,
  sb6: { always: [], conditional: [] },
  std_custom: {
    always: ['pim_publish', 'piq_quickpart', 'web_publish'],
    conditional: [],
  },
  discontinue: { always: ['web_publish', 'piq_quickpart', 'pim_publish'], conditional: [] },
  bundle_edit: {
    always: ['sku', 'uin', 'web_image', 'product_review', 'pim_publish', 'web_publish'],
    conditional: [],
  },
}

export function requiredStepsForChanges(types: OriginUpdateType[]): Set<string> {
  const out = new Set<string>()
  for (const t of types) {
    for (const k of MODIFY_DELIVERABLES[t]?.always ?? []) out.add(k)
  }
  return out
}

export function conditionalStepsForChanges(types: OriginUpdateType[]): Set<string> {
  const out = new Set<string>()
  for (const t of types) {
    for (const k of MODIFY_DELIVERABLES[t]?.conditional ?? []) out.add(k)
  }
  return out
}

/**
 * New attribute values implied by a change request — reference modify.ts L78–147.
 * Pre-fills PMA attributes and drives the "Modified" pill.
 */
export function changedAttributeValues(changes: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}

  const price = changes.price as { new_price?: number } | undefined
  if (price?.new_price != null) out.price = `$${Number(price.new_price).toFixed(2)}`

  const uin = changes.uin as { uin_action?: string; new_uin?: string | null } | undefined
  if (uin) out.uin = uin.uin_action === 'remove' ? '(removed)' : (uin.new_uin ?? '')

  const spec = changes.spec as {
    new_substrate?: string | null
    new_width?: number | null
    new_height?: number | null
    single_double?: string | null
    new_finishing?: string | null
    taping?: string | null
    rounded_corners?: string | null
    mounting_holes?: { location?: string; diameter?: string } | null
    hardware?: string | boolean | null
    grommets?: string | null
    hem?: string | null
  } | undefined
  if (spec) {
    if (spec.new_substrate) out.base_substrate = spec.new_substrate
    if (spec.new_width != null) out.width = `${spec.new_width}"`
    if (spec.new_height != null) out.height = `${spec.new_height}"`
    if (spec.single_double) {
      out.single_double =
        spec.single_double === 'double'
          ? 'Double-sided'
          : spec.single_double === 'single'
            ? 'Single-sided'
            : 'No Print'
    }
    if (spec.new_finishing) out.laminate = spec.new_finishing
    if (spec.taping) out.taping = spec.taping
    if (spec.rounded_corners) out.rounded_corners = spec.rounded_corners
    if (spec.mounting_holes) {
      out.mounting_holes = `Yes · ${spec.mounting_holes.location ?? ''} · ${spec.mounting_holes.diameter ?? ''}`
    }
    if (spec.hardware != null && spec.hardware !== false) {
      out.hardware_needed = 'Yes'
      if (typeof spec.hardware === 'string' && spec.hardware.trim()) {
        out.hardware_description = spec.hardware.trim()
      }
    }
    if (spec.grommets) out.grommets = spec.grommets
    if (spec.hem) out.hem = spec.hem
  }

  const design = changes.design as {
    graphic_method?: string | null
    vinyl_type?: string | null
    rta_color?: string | null
    white_ink?: string[] | null
    reference_sku?: { sku?: string; label?: string } | null
  } | undefined
  if (design) {
    if (design.graphic_method) out.graphic_method = design.graphic_method
    if (design.vinyl_type) out.vinyl_type = design.vinyl_type
    if (design.rta_color) out.rta_color = design.rta_color
    if (design.white_ink?.length) out.white_ink = design.white_ink.join(', ')
    if (design.reference_sku?.sku) {
      out.reference_sku = design.reference_sku.label
        ? `${design.reference_sku.sku} — ${design.reference_sku.label}`
        : design.reference_sku.sku
    }
  }

  const stdCustom = changes.std_custom as { is_standard_custom?: boolean } | undefined
  if (stdCustom?.is_standard_custom) out.std_type = 'Standard-Custom'

  return out
}
